import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";

interface PricingPlan {
  name: string;
  price: string;
  description: string;
  features: string[];
  cta: string;
  highlighted: boolean;
  planId?: string;
}

interface PricingCardProps {
  plan: PricingPlan;
  index: number;
}

const PricingCard = ({ plan, index }: PricingCardProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const handleGetStarted = async () => {
    if (!user) {
      // If not logged in, redirect to login
      navigate("/login");
      return;
    }
    
    setIsLoading(true);
    
    try {
      // If it's the free plan, just redirect to home
      if (plan.price === "Free") {
        navigate("/home");
        return;
      }
      
      // For paid plans, redirect to Stripe checkout
      if (plan.planId) {
        const { data, error } = await supabase.functions.invoke("create-checkout", {
          body: {
            planId: plan.planId,
            origin: window.location.origin,
          },
        });
        
        if (error) {
          console.error("Checkout error:", error);
          toast.error("Failed to create checkout session");
          return;
        }
        
        // Redirect to Stripe checkout
        if (data?.url) {
          window.location.href = data.url;
        } else {
          toast.error("No checkout URL returned");
        }
      } else {
        toast.error("Plan ID is missing");
      }
    } catch (error) {
      console.error("Error during checkout:", error);
      toast.error("An error occurred during checkout");
    } finally {
      setIsLoading(false);
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 }, // Increased y from 20 to 30
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring",
        stiffness: 100,
        damping: 15,
        delay: index * 0.1
      }
    },
    hover: { 
      y: -10,
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      transition: { type: "spring", stiffness: 400, damping: 17 }
    }
  };

  // Customize the card based on the plan type
  const getCardStyle = () => {
    if (plan.highlighted) {
      return {
        borderColor: "border-primary",
        shadow: "shadow-lg shadow-primary/10",
        bgGradient: "bg-gradient-to-b from-card to-primary/5" 
      };
    }
    
    if (plan.name === "Premium") {
      return {
        borderColor: "border-green-500",
        shadow: "shadow-md shadow-green-500/10",
        bgGradient: "bg-gradient-to-b from-card to-green-500/5"
      };
    }
    
    return {
      borderColor: "border-border",
      shadow: "shadow-sm",
      bgGradient: "bg-card"
    };
  };

  const { borderColor, shadow, bgGradient } = getCardStyle();

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover="hover"
      className="flex flex-col h-full"
    >
      <Card className={`flex flex-col h-full ${borderColor} ${shadow} relative overflow-hidden ${plan.highlighted ? 'z-10' : ''}`}>
        {/* Gradient background for card */}
        <div className={`absolute inset-0 ${bgGradient} -z-10`}></div>
        
        {/* Popular tag if highlighted */}
        {plan.highlighted && (
          <div className="absolute top-0 right-0">
            <div className="bg-primary text-primary-foreground text-xs py-1 px-3 rounded-bl-lg rounded-tr-lg font-medium flex items-center">
              <Sparkles className="h-3 w-3 mr-1" />
              MOST POPULAR
            </div>
          </div>
        )}
        
        <CardHeader className={`${plan.highlighted ? 'pb-8' : 'pb-6'}`}>
          <CardTitle className="text-left text-2xl">{plan.name}</CardTitle>
          <div className="flex items-baseline text-left mt-2">
            <span className="text-4xl font-bold">{plan.price.split('/')[0]}</span>
            {plan.price !== "Free" && (
              <span className="ml-2 text-muted-foreground text-sm">/month</span>
            )}
          </div>
          <CardDescription className="text-left mt-2">{plan.description}</CardDescription>
        </CardHeader>
        
        <CardContent className="flex-grow">
          <ul className="space-y-3">
            {plan.features.map((feature, i) => (
              <li key={i} className="flex items-start">
                <Check className={`h-5 w-5 ${plan.highlighted ? 'text-primary' : 'text-green-500'} mr-2 flex-shrink-0`} />
                <span className="text-sm text-left">{feature}</span>
              </li>
            ))}
          </ul>
        </CardContent>
        
        <CardFooter className="pt-6 pb-8">
          <Button 
            onClick={handleGetStarted}
            variant={plan.highlighted ? "default" : "outline"}
            className={`w-full h-11 ${plan.highlighted ? 'bg-primary hover:bg-primary/90 text-primary-foreground' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="mr-2">Processing</span>
                <div className="h-4 w-4 rounded-full border-2 border-current border-r-transparent animate-spin" />
              </>
            ) : (
              plan.cta
            )}
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
};

export default PricingCard;
